// @fileoverview : 기준정보 > 라우팅
import { mutateRoutings } from "@/api/routing/useRoutingQuery";
import { consumeTypeColor, routingTypeColor } from "@/constants/routings";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { MoldDetailForm } from "@/features/mold/form/moldDetailForm";
import { OperationDetailForm } from "@/features/operations/components/form/operationDetailForm";
import { RoutingCreateForm } from "@/features/routing/components/form/RoutingCreateForm";
import { RoutingDetailForm } from "@/features/routing/components/form/RoutingDetailForm";
import { RoutingEditForm } from "@/features/routing/components/form/RoutingUpdateForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import { ActionHeader } from "@/features/standardLayout/StandardInfoActionHeader";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { css } from "@emotion/css";
import { ActionIcon, Avatar, Badge, Flex, Text, Tooltip } from "@mantine/core";
import {
  DefaultApiRoutingsRoutingIdDeleteRequest,
  ItemsGet200ResponseRowsInnerRoutingsInner,
  RoutingsGet200Response,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const Routing = () => {
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();
  const { selectedRows, refetch } = useStandardLayout();
  const { mutate: deleteMutate } = useMutation(
    (params: DefaultApiRoutingsRoutingIdDeleteRequest) =>
      mutateRoutings
        .delete(params)
        .mutationFn(params as DefaultApiRoutingsRoutingIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          `${t("message.deleteSuccess", { item: `${t("routing.routing")}` })}`,
          `${t("common.deleteSuccess")}`,
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("routing.code") }),
          `${t("common.deleteFail", { item: `${t("defect.defect")}` })}`,
          "red",
        );
      },
    },
  );

  const columns: readonly Column<ItemsGet200ResponseRowsInnerRoutingsInner>[] = useMemo(
    () => [
      {
        key: "code",
        name: t("routing.code"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() => {
                const detailRoutingActionView = () => {
                  openModal(
                    <RoutingDetailForm routingCode={formatterProps.row?.code} />,
                    null,
                    `${t("common.detail", { item: `${t("routing.routing")}` })}`,
                  );
                };
                detailRoutingActionView();
              }}
            >
              {formatterProps.row.code ?? "-"}
            </LinkAnchor>
          );
        },
      },
      {
        ...SelectColumn,
        resizable: true,
      },
      {
        key: "itemName",
        name: t("item.name"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <ItemsDetailForm itemCode={formatterProps.row.itemCode ?? ""} />,
                  null,
                  `${t("common.detail", { item: `${t("item.item")}` })}`,
                )
              }
            >
              {formatterProps.row.item?.name ?? "-"}
            </LinkAnchor>
          );
        },
      },
      {
        key: "itemCode",
        name: t("item.code"),
        sortable: true,
        resizable: true,
      },
      {
        key: "spec",
        name: t("item.spec"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return <Flex>{formatterProps.row.item?.spec}</Flex>;
        },
      },
      {
        key: "operationCode",
        name: t("operation.code"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <OperationDetailForm operationCode={formatterProps.row.operationCode} />,
                  null,
                  "",
                )
              }
            >
              {formatterProps.row.operationCode}
            </LinkAnchor>
          );
        },
      },

      {
        key: "routingType",
        name: t("routing.type"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <Badge size="lg" color={routingTypeColor[formatterProps.row?.routingType!]}>
              {t(formatterProps.row?.routingType!)}
            </Badge>
          );
        },
      },
      {
        key: "moldCode",
        name: t("mold.code"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(<MoldDetailForm moldCode={formatterProps.row.moldCode} />, null, "")
              }
            >
              {formatterProps.row.moldCode}
            </LinkAnchor>
          );
        },
      },
      {
        key: "consumeType",
        name: t("routing.consumeType"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => (
          <Badge size="lg" color={consumeTypeColor[formatterProps.row?.consumeType!]}>
            {t(formatterProps.row?.consumeType!)}
          </Badge>
        ),
      },
      {
        key: "seq",
        name: t("routing.seq"),
        sortable: true,
        resizable: true,
        cellClass: css`
          justify-content: right;
        `,
      },
      {
        key: "options",
        name: t("common.action"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          const detailRoutingActionView = () => {
            openModal(
              <RoutingDetailForm routingCode={formatterProps.row?.code} />,
              null,
              `${t("common.detail", { item: `${t("routing.routing")}` })}`,
            );
          };

          const updateRoutingActionView = () => {
            openModal(
              <RoutingEditForm formatterProps={formatterProps?.row} />,
              null,
              `${t("common.edit", { item: `${t("routing.routing")}` })}`,
              true,
            );
          };

          const deleteRoutingAction = () => {
            openModal(
              <ConfirmForm
                message={t("message.rowDelete")}
                yesCallback={() => {
                  deleteMutate({ routingId: formatterProps.row?.id as number });
                }}
                noCallback={() => {}}
                commonCallback={() => closeModal(null)}
              />,
              null,
              `${t("common.delete", { item: `${t("routing.routing")}` })}`,
            );
          };

          return (
            <Flex w="100%" h="100%" justify="center" align="center" gap="md">
              <ActionIcon variant="subtle" onClick={detailRoutingActionView} color="blue">
                <IconSearch />
              </ActionIcon>
              <ActionIcon variant="subtle" onClick={updateRoutingActionView} color="yellow">
                <IconEdit />
              </ActionIcon>
              <ActionIcon variant="subtle" onClick={() => deleteRoutingAction()} color="red">
                <IconTrash />
              </ActionIcon>
            </Flex>
          );
        },
      },
    ],
    [t, closeModal, deleteMutate, openModal, t],
  );

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        `${t("message.deleteSelect", { item: `${t("routing.routing")}` })}`,
        `${t("common.deleteFail", { item: `${t("routing.routing")}` })}`,
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ routingId: row });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: `${t("routing.routing")}` })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <ActionHeader
          createFn={() =>
            openModal(
              <RoutingCreateForm />,
              null,
              `${t("common.additional", { item: `${t("routing.routing")}` })}`,
              true,
            )
          }
          createDataTitle={t("routing.routing")}
          deleteFn={() => handleDelete()}
        />
      </Flex>
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          {t("common.list", { item: `${t("routing.routing")}` })}
        </Text>
        <Text fz="xs">{t("common.subTitle", { item: `${t("routing.routing")}` })}</Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<RoutingsGet200Response, ItemsGet200ResponseRowsInnerRoutingsInner>
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<RoutingsGet200Response> />
      </Flex>
    </Flex>
  );
};
